import { lazy, Suspense, useState } from "react";
import { theme } from "@duncancooper/brand";
import { FlatLogo } from "./FlatLogo.js";
import styles from "../styles/components/BrandLogo.module.css";

/** Wordmark rendered between the brand semicircles. */
export const SITE_LOGO_TEXT = "twice-daily";

/**
 * Box the 3D mark is framed into, in px. The headers are 44px tall; the mark
 * fills roughly 90% of the box height, so this lands a little larger than the
 * old 24px flat logo. The width suits the short "td" wordmark (the engine
 * centres the mark, so a wider box would only add empty space).
 */
export const SITE_LOGO_HEIGHT = 42;
export const SITE_LOGO_WIDTH = 220;

// The logo engine pulls in three.js, so it lives in its own lazy chunk.
const SiteLogo3D = lazy(() => import("./SiteLogo3D.js"));

export interface BrandLogoProps {
  /** Semicircle color. Follows the active (or hovered) theme's primary. */
  accentColor?: string;
  textColor: string;
  backgroundColor: string;
}

/**
 * Lightweight stand-in shown while the logo chunk loads: the two brand
 * semicircles joined, in the same box the engine will draw into.
 */
function LogoPlaceholder({ accentColor }: { accentColor: string }) {
  const disc = SITE_LOGO_HEIGHT * 0.55;
  const half = disc / 2;
  return (
    <span
      aria-hidden="true"
      className={styles.placeholder}
      style={{ width: SITE_LOGO_WIDTH, height: SITE_LOGO_HEIGHT }}
    >
      <i
        style={{
          width: half,
          height: disc,
          background: accentColor,
          borderRadius: `${disc}px 0 0 ${disc}px`,
        }}
      />
      <i
        style={{
          width: half,
          height: disc,
          background: accentColor,
          borderRadius: `0 ${disc}px ${disc}px 0`,
        }}
      />
    </span>
  );
}

function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The "td" header logo, rendered by @duncancooper/brand's 3D logo engine the
 * way www.tuit.dev renders its header mark. Falls back to the flat logo when
 * WebGL is unavailable.
 */
export function BrandLogo({
  accentColor,
  textColor,
  backgroundColor,
}: BrandLogoProps) {
  const [webgl] = useState(hasWebGL);
  const [animate] = useState(() => !prefersReducedMotion());
  // The engine parses colors with three.js, so it needs a real value rather
  // than a CSS variable.
  const shapeColor = accentColor ?? theme.colors.primary;

  if (!webgl) {
    return (
      <FlatLogo
        size={24}
        accentColor={shapeColor}
        textColor={textColor}
        text={SITE_LOGO_TEXT}
        separation={-12}
      />
    );
  }
  return (
    <Suspense fallback={<LogoPlaceholder accentColor={shapeColor} />}>
      <SiteLogo3D
        accentColor={shapeColor}
        textColor={textColor}
        backgroundColor={backgroundColor}
        animate={animate}
      />
    </Suspense>
  );
}
